import { LoaderCircle } from "lucide-react";
import { dynamicIconImports } from "lucide-react/dynamic";
import type { ComponentType, SVGProps } from "react";
import { Suspense, use } from "react";

/** PascalCase Lucide icon component name (e.g. `LoaderCircle`, `LoaderCircle`). */
export type IconName = string;

interface IIconProps {
	name: IconName | undefined | null;
	color?: string;
	weight?: "thin" | "light" | "normal" | "medium" | "semibold" | "bold";
	className?: string;
	fill?: string;
}

const warnedIcons = new Set<string>();

type SvgIcon = ComponentType<SVGProps<SVGSVGElement>>;

const dynamicImportMap = dynamicIconImports as Record<
	string,
	() => Promise<{ default: SvgIcon }>
>;

const iconPromises = new Map<string, Promise<SvgIcon>>();

function pascalToKebabCase(str: string): string {
	if (!str) return "";
	return str
		.replace(/([a-z])([A-Z])/g, "$1-$2")
		.replace(/([A-Z])([A-Z][a-z])/g, "$1-$2")
		.replace(/([a-zA-Z])([0-9])/g, "$1-$2")
		.toLowerCase();
}

function loadIcon(pascalName: string): Promise<SvgIcon> {
	const effective = pascalName.trim() || "LoaderCircle";
	const cached = iconPromises.get(effective);
	if (cached) {
		return cached;
	}
	const kebab = pascalToKebabCase(effective);
	const loader = dynamicImportMap[kebab] ?? dynamicImportMap["circle-alert"];
	const p = loader().then((m) => m.default);
	iconPromises.set(effective, p);
	return p;
}

function IconInner(
	props: IIconProps & {
		strokeWidth: string;
	},
) {
	const iconName = props.name ?? "";
	const LucideIcon = use(loadIcon(iconName));
	return (
		<LucideIcon
			className={props.className}
			color={props.color}
			strokeWidth={props.strokeWidth}
			{...(props.fill ? { fill: props.fill } : {})}
		/>
	);
}

export default function Icon(props: IIconProps) {
	const iconName = props.name ?? "";
	const trimmed = iconName.trim();
	const kebab = pascalToKebabCase(trimmed);
	if (trimmed && !(kebab in dynamicImportMap) && !warnedIcons.has(trimmed)) {
		warnedIcons.add(trimmed);
		console.warn(`[Icon] Unknown icon name: "${trimmed}"`);
	}

	const weight = props.weight || "normal";
	const strokeWidth = parseWeight(weight);
	const shared = {
		className: props.className,
		color: props.color,
		strokeWidth,
		...(props.fill ? { fill: props.fill } : {}),
	};

	return (
		<Suspense fallback={<LoaderCircle {...shared} />}>
			<IconInner {...props} strokeWidth={strokeWidth} />
		</Suspense>
	);
}

function parseWeight(weight: string) {
	switch (weight) {
		case "thin":
			return "0.5";
		case "light":
			return "1";
		case "normal":
			return "2";
		case "medium":
			return "3";
		case "semibold":
			return "4";
		case "bold":
			return "5";
		default:
			return "2";
	}
}
